import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatCard, fmtEUR, fmtInt } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Crown, Heart, Users, UserPlus, Ghost, Stamp, Gift, Settings2 } from "lucide-react";
import { useLists } from "@/hooks/useLists";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_KINDS = ["residente", "turista", "hotel", "vip", "occasionale"];

const SEGMENT_ICON = { vip: Crown, abituale: Heart, nuovo: UserPlus, occasionale: Users, inattivo: Ghost };
const SEGMENT_COLOR = {
  vip: "bg-amber-100 text-amber-900 border-amber-300",
  abituale: "bg-emerald-100 text-emerald-900 border-emerald-300",
  nuovo: "bg-sky-100 text-sky-900 border-sky-300",
  occasionale: "bg-neutral-100 text-neutral-800 border-neutral-300",
  inattivo: "bg-red-100 text-red-900 border-red-300",
};
const SEGMENT_LABEL = { vip: "VIP", abituale: "Abituali", nuovo: "Nuovi", occasionale: "Occasionali", inattivo: "Inattivi" };

export default function Crm() {
  const { lists } = useLists();
  const { can } = useAuth();
  // FASE 7.4 STEP F.1 — CRM mutations gate on crm.write.
  const canWrite = can("crm.write");
  const KINDS = lists?.customer_kinds?.length ? lists.customer_kinds : DEFAULT_KINDS;
  const [items, setItems] = useState([]);
  const [segments, setSegments] = useState(null);
  const [q, setQ] = useState("");
  const [seg, setSeg] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", kind: "residente", avg_spend: 0, visits_count: 1, notes: "" });
  const [loyaltyOpen, setLoyaltyOpen] = useState(null); // customer object

  const load = async () => {
    const [c, s] = await Promise.all([api.get("/customers"), api.get("/customers/segments")]);
    setItems(c.data); setSegments(s.data);
  };
  useEffect(() => { load(); }, []);

  const addStamp = async (c, count = 1) => {
    const r = await api.post(`/customers/${c.id}/stamp`, { count });
    if (r.data.rewards_available > (c.rewards_available || 0)) {
      toast.success(`🎉 Premio sbloccato! Riscattabile ora.`);
    } else {
      toast.success(`+${count} stamp (${r.data.stamps}/${r.data.goal})`);
    }
    // update local + refresh full item to get new history
    const fresh = await api.get("/customers");
    setItems(fresh.data);
    const updated = fresh.data.find((x) => x.id === c.id);
    if (updated && loyaltyOpen?.id === c.id) setLoyaltyOpen(updated);
  };
  const redeem = async (c) => {
    const reward = window.prompt("Descrizione premio (es. Cappuccino gratis):", "Cappuccino gratis");
    if (!reward) return;
    try {
      await api.post(`/customers/${c.id}/redeem`, { reward });
      toast.success("Premio riscattato");
      const fresh = await api.get("/customers");
      setItems(fresh.data);
      const updated = fresh.data.find((x) => x.id === c.id);
      if (updated && loyaltyOpen?.id === c.id) setLoyaltyOpen(updated);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const setGoal = async (c) => {
    const g = window.prompt("Ogni quanti stamp scatta un premio?", String(c.stamps_goal || 10));
    if (!g) return;
    await api.put(`/customers/${c.id}/loyalty-goal`, { stamps_goal: parseInt(g) });
    toast.success("Obiettivo aggiornato");
    const fresh = await api.get("/customers");
    setItems(fresh.data);
    const updated = fresh.data.find((x) => x.id === c.id);
    if (updated && loyaltyOpen?.id === c.id) setLoyaltyOpen(updated);
  };

  const filtered = useMemo(() => items.filter((c) => {
    if (seg !== "all" && c.segment !== seg) return false;
    if (q && !`${c.first_name} ${c.last_name} ${c.email}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [items, seg, q]);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/customers", { ...form, avg_spend: Number(form.avg_spend), visits_count: Number(form.visits_count) });
    toast.success("Cliente aggiunto"); setOpen(false); load();
  };
  const del = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/customers/${id}`); load(); };
  const recordVisit = async (id) => {
    const s = window.prompt("Importo spesa in €:");
    if (!s) return;
    await api.post(`/customers/${id}/visit`, { spend: parseFloat(s) });
    toast.success("Visita registrata"); load();
  };

  return (
    <div data-testid="crm-page">
      <PageHeader eyebrow="Clienti" title="CRM"
        subtitle="Segmenta clienti per fidelizzare gli abituali, coccolare i VIP e recuperare gli inattivi."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            {canWrite && (
            <DialogTrigger asChild><Button data-testid="new-customer-btn" className="bg-neutral-900 text-[#FDFBF7]"><Plus size={14} strokeWidth={1.5} /> Nuovo cliente</Button></DialogTrigger>
            )}
            <DialogContent>
              <DialogHeader><DialogTitle className="font-serif">Nuovo cliente</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-3">
                <div><Label>Nome</Label><Input data-testid="cust-first" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
                <div><Label>Cognome</Label><Input data-testid="cust-last" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Telefono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>Tipo cliente</Label>
                  <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                    <SelectTrigger data-testid="cust-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Spesa media (€)</Label><Input type="number" step="0.01" value={form.avg_spend} onChange={(e) => setForm({ ...form, avg_spend: e.target.value })} /></div>
                <div><Label>Visite finora</Label><Input type="number" value={form.visits_count} onChange={(e) => setForm({ ...form, visits_count: e.target.value })} /></div>
                <div className="col-span-2"><Label>Note</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <DialogFooter className="col-span-2"><Button data-testid="cust-submit" type="submit" className="bg-neutral-900 text-[#FDFBF7]">Salva</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        } />

      {segments && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {Object.entries(SEGMENT_LABEL).map(([k, label]) => {
            const Icon = SEGMENT_ICON[k];
            return (
              <button key={k} onClick={() => setSeg(seg === k ? "all" : k)}
                data-testid={`seg-${k}`}
                className={`border rounded-lg p-4 text-left transition-all ${SEGMENT_COLOR[k]} ${seg === k ? "ring-2 ring-neutral-900" : "hover:opacity-90"}`}>
                <div className="flex items-center gap-2 label-eyebrow"><Icon size={12} strokeWidth={1.5} /> {label}</div>
                <div className="font-serif text-3xl mt-2">{segments.segments[k] || 0}</div>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex gap-3 mb-4">
        <Input data-testid="crm-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente…" className="max-w-xs" />
        <Select value={seg} onValueChange={setSeg}>
          <SelectTrigger className="max-w-xs" data-testid="crm-segment"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i segmenti</SelectItem>
            {Object.entries(SEGMENT_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-[#E2D9C8] rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-sm zebra min-w-[720px]">
          <thead><tr className="text-left label-eyebrow">
            <th className="p-3">Cliente</th><th className="p-3">Segmento</th>
            <th className="p-3 text-right">Visite</th><th className="p-3 text-right">Spesa media</th>
            <th className="p-3">Loyalty</th>
            <th className="p-3 text-right">Ultima visita</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {filtered.map((c) => {
              const goal = c.stamps_goal || 10;
              const stamps = c.stamps || 0;
              const rewardsAvail = c.rewards_available || 0;
              const pct = Math.min(100, Math.round((stamps / goal) * 100));
              return (
              <tr key={c.id} className="border-t border-[#E2D9C8]" data-testid={`cust-${c.id}`}>
                <td className="p-3">
                  <div className="font-medium">{c.first_name} {c.last_name}</div>
                  <div className="text-xs text-neutral-500">{c.email}</div>
                </td>
                <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full border ${SEGMENT_COLOR[c.segment]}`}>{SEGMENT_LABEL[c.segment]}</span></td>
                <td className="p-3 text-right">{fmtInt(c.visits_count)}</td>
                <td className="p-3 text-right font-medium">{fmtEUR(c.avg_spend)}</td>
                <td className="p-3">
                  <button onClick={() => setLoyaltyOpen(c)} data-testid={`loyalty-${c.id}`} className="group text-left">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium">{stamps}/{goal}</span>
                      {rewardsAvail > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 rounded-full">
                          <Gift size={9} strokeWidth={1.8} /> {rewardsAvail}
                        </span>
                      )}
                    </div>
                    <div className="w-20 h-1.5 bg-[#F3EDE1] rounded-full mt-1 overflow-hidden">
                      <div className={`h-full ${rewardsAvail > 0 ? "bg-amber-500" : "bg-neutral-800"} group-hover:opacity-80`} style={{ width: `${pct}%` }} />
                    </div>
                  </button>
                </td>
                <td className="p-3 text-right text-xs">{c.days_since_visit !== null ? `${c.days_since_visit}g fa` : "—"}</td>
                <td className="p-3 text-right space-x-1 whitespace-nowrap">
                  {canWrite && (
                    <>
                      <button onClick={() => addStamp(c, 1)} data-testid={`stamp-${c.id}`} title="Aggiungi stamp"
                        className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-[#E2D9C8] rounded hover:border-neutral-900">
                        <Stamp size={12} strokeWidth={1.5} /> +Stamp
                      </button>
                      <button onClick={() => recordVisit(c.id)} data-testid={`visit-${c.id}`} className="text-xs px-2 py-1 border border-[#E2D9C8] rounded hover:border-neutral-900">+ Visita</button>
                      <button onClick={() => del(c.id)} className="text-neutral-400 hover:text-red-700" data-testid={`del-cust-${c.id}`}><Trash2 size={14} strokeWidth={1.5} /></button>
                    </>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Loyalty Card Dialog */}
      <Dialog open={!!loyaltyOpen} onOpenChange={(v) => !v && setLoyaltyOpen(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2"><Stamp size={16} strokeWidth={1.5} /> Loyalty Card</DialogTitle>
          </DialogHeader>
          {loyaltyOpen && (() => {
            const goal = loyaltyOpen.stamps_goal || 10;
            const stamps = loyaltyOpen.stamps || 0;
            const rewardsAvail = loyaltyOpen.rewards_available || 0;
            const rewardsRedeemed = loyaltyOpen.rewards_redeemed || 0;
            const remaining = Math.max(0, goal - stamps);
            const history = (loyaltyOpen.stamps_history || []).slice().reverse().slice(0, 10);
            return (
              <div className="space-y-4">
                <div className="border border-[#E2D9C8] rounded-lg p-5 bg-gradient-to-br from-[#F3EDE1] to-[#FDFBF7]">
                  <div className="label-eyebrow">Cliente</div>
                  <div className="font-serif text-xl mt-1">{loyaltyOpen.first_name} {loyaltyOpen.last_name}</div>
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-neutral-500">Progresso verso il prossimo premio</span>
                      <span className="text-sm font-medium">{stamps}/{goal}</span>
                    </div>
                    <div className="grid grid-cols-10 gap-1">
                      {Array.from({ length: goal }).map((_, i) => (
                        <div key={i} className={`aspect-square rounded-full border-2 ${i < stamps ? "bg-neutral-900 border-neutral-900" : "border-[#D7C4A3] bg-white"}`} />
                      ))}
                    </div>
                    <div className="mt-2 text-xs text-neutral-600">
                      {remaining === 0 ? "🎉 Premio pronto per il riscatto!" : `${remaining} stamp mancanti al prossimo premio`}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className={`border rounded-lg p-3 ${rewardsAvail > 0 ? "bg-amber-50 border-amber-300" : "border-[#E2D9C8] bg-white"}`}>
                    <div className="label-eyebrow flex items-center gap-1"><Gift size={11} /> Disponibili</div>
                    <div className={`font-serif text-3xl mt-1 ${rewardsAvail > 0 ? "text-amber-800" : ""}`}>{rewardsAvail}</div>
                  </div>
                  <div className="border border-[#E2D9C8] rounded-lg p-3 bg-white">
                    <div className="label-eyebrow">Riscattati</div>
                    <div className="font-serif text-3xl mt-1">{rewardsRedeemed}</div>
                  </div>
                </div>

                <div className="flex gap-2 flex-wrap">
                  {canWrite && (
                  <Button onClick={() => addStamp(loyaltyOpen, 1)} data-testid="loyalty-add-1" className="bg-neutral-900 text-[#FDFBF7]">
                    <Stamp size={14} strokeWidth={1.5} /> +1 Stamp
                  </Button>
                  )}
                  {canWrite && (<Button onClick={() => addStamp(loyaltyOpen, 5)} data-testid="loyalty-add-5" variant="outline">+5</Button>)}
                  {canWrite && (
                  <Button onClick={() => redeem(loyaltyOpen)} data-testid="loyalty-redeem"
                    disabled={rewardsAvail <= 0}
                    className={rewardsAvail > 0 ? "bg-amber-600 text-white hover:bg-amber-700" : ""}>
                    <Gift size={14} strokeWidth={1.5} /> Riscatta premio
                  </Button>
                  )}
                  {canWrite && (
                  <Button onClick={() => setGoal(loyaltyOpen)} variant="outline" title="Cambia obiettivo" data-testid="loyalty-set-goal">
                    <Settings2 size={14} strokeWidth={1.5} />
                  </Button>
                  )}
                </div>

                {history.length > 0 && (
                  <div>
                    <div className="label-eyebrow mb-2">Storico (ultimi 10)</div>
                    <div className="max-h-52 overflow-y-auto border border-[#E2D9C8] rounded-lg divide-y divide-[#E2D9C8]">
                      {history.map((h) => (
                        <div key={h.id} className="p-2 flex items-center gap-2 text-xs">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                            h.type === "stamp" ? "bg-neutral-900 text-white" :
                            h.type === "redeem" ? "bg-amber-100 text-amber-900" :
                            "bg-emerald-100 text-emerald-900"}`}>
                            {h.type === "stamp" ? <Stamp size={11} strokeWidth={2} /> : h.type === "redeem" ? <Gift size={11} strokeWidth={2} /> : "★"}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium">
                              {h.type === "stamp" ? `+${h.count} stamp` : h.type === "redeem" ? "Premio riscattato" : "Premio sbloccato"}
                            </div>
                            {h.note && <div className="text-neutral-500 truncate">{h.note}</div>}
                          </div>
                          <div className="text-neutral-400 shrink-0">{(h.at || "").slice(0, 10)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
